/**
 * Pipeline State Derivation Hook
 *
 * Derives PipelineState from Note API response fields.
 * Uses status + intent fields to infer which stages have completed.
 */

import { useMemo } from "react";
import type { NoteApiResponse } from "../types/api";
import type {
  PipelineState,
  PipelineStage,
  StageStatus,
  IntentCategory,
} from "../types/pipeline";
import { ROUTING_DECISIONS } from "../types/pipeline";

/**
 * Get the routing decision string for an intent category.
 */
function getRoutingDecision(intent: IntentCategory): string {
  return ROUTING_DECISIONS[intent];
}

/**
 * Derive pipeline stage statuses from Note fields.
 *
 * State machine logic:
 * - INPUT: Always completed if note exists
 * - PII_SCAN: Completed if intent is set (guardrails passed)
 * - GUARDRAILS: Completed if intent set, failed if status is FAILED
 * - CLASSIFY: Completed if intent set, active if PROCESSING without intent
 * - QUEUE: Active during PROCESSING after classify; SKIPPED for SIMPLE_NOTE
 *   (no Celery dispatch — see service.py:379)
 * - RETRIEVE: Active during PROCESSING; SKIPPED for SIMPLE_NOTE (no worker)
 * - LLM: Active during processing; SKIPPED for SEARCH_ONLY (retrieval only)
 *   and SIMPLE_NOTE (worker bypassed)
 * - REVIEW: Active when REVIEW status; SKIPPED for SIMPLE_NOTE (auto-completed)
 * - STORAGE: Completed when status is COMPLETED (always — SIMPLE_NOTE writes
 *   the Note row directly)
 * - EMBEDDING: Gated by review accept/edit (service.py:705 dispatches
 *   `_dispatch_enrichment_tasks`); SKIPPED for SIMPLE_NOTE (never reviews)
 * - RECOMMEND: Same enrichment gate as EMBEDDING; SKIPPED for SIMPLE_NOTE
 *   and for DISCARDED notes (no embedding → no recs)
 */
function deriveStageStatus(
  note: NoteApiResponse,
): Record<PipelineStage, StageStatus> {
  const { status, intent } = note;

  // Helper to determine if classification is complete
  const classificationComplete = intent !== null;

  // SIMPLE_NOTE bypasses worker entirely. apps/api .../notes/service.py
  // flips status to COMPLETED inside `NotesService._route_by_intent()`
  // (function entry at :343, SIMPLE_NOTE branch at :379) — no Celery dispatch,
  // so QUEUE/RETRIEVE/LLM never fire. REVIEW is also skipped (auto-completed,
  // no RLHF capture). Enrichment (EMBEDDING + RECOMMEND) is dispatched only
  // from review accept/edit by `_dispatch_enrichment_tasks()` at :705, which
  // SIMPLE_NOTE never reaches. STORAGE still "completes" because the Note row
  // is written to PG.
  const isSimpleNote = intent === "SIMPLE_NOTE";

  return {
    // INPUT: Always completed if we have a note
    INPUT: "completed",

    // PII_SCAN: Part of guardrails preprocessing
    // Completed when guardrails processing finishes (intent is set)
    PII_SCAN: classificationComplete
      ? "completed"
      : status === "FAILED"
        ? "failed"
        : "active",

    // GUARDRAILS: Completed if we have classification result
    // Failed if status is FAILED (could be injection blocked)
    GUARDRAILS:
      status === "FAILED"
        ? "failed"
        : classificationComplete
          ? "completed"
          : "active",

    // CLASSIFY: Part of guardrails service (L1 layer)
    // Active during PROCESSING until intent is set
    CLASSIFY: classificationComplete
      ? "completed"
      : status === "PROCESSING" || status === "PENDING"
        ? "active"
        : "pending",

    // QUEUE: Active during PROCESSING after classification (Worker picks up
    // the job). Skipped for SIMPLE_NOTE — no Celery dispatch in service.py:379.
    QUEUE: isSimpleNote
      ? "skipped"
      : status === "PROCESSING" && classificationComplete
        ? "active"
        : status === "COMPLETED"
          ? "completed"
          : "pending",

    // RETRIEVE: Hybrid search (pgvector + BM25 → RRF) for RAG context injection.
    // Runs between QUEUE and LLM for SUMMARIZE/EXTRACT/REASON. For SEARCH_ONLY
    // this is the main processing stage (LLM is skipped). Skipped entirely for
    // SIMPLE_NOTE — worker never runs (service.py:379).
    RETRIEVE: isSimpleNote
      ? "skipped"
      : status === "COMPLETED" || status === "REVIEW"
        ? "completed"
        : status === "PROCESSING" && classificationComplete
          ? "active"
          : "pending",

    // LLM: Skipped for SEARCH_ONLY (retrieval-only, results go to REVIEW) and
    // SIMPLE_NOTE (status flips to COMPLETED in `NotesService._route_by_intent()`
    // before any worker dispatch — service.py:379). All other intents traverse
    // LLM. Earlier comment claimed "D-015: no SIMPLE_NOTE skip" — that pre-dated
    // the SIMPLE_NOTE optimization that saves ~5GB Ollama RAM per request.
    LLM:
      isSimpleNote || intent === "SEARCH_ONLY"
        ? "skipped"
        : status === "COMPLETED" || status === "REVIEW"
          ? "completed"
          : status === "PROCESSING" && classificationComplete
            ? "active"
            : "pending",

    // REVIEW: Active when status is REVIEW (user must confirm LLM output).
    // SUMMARIZE/EXTRACT/REASON go through REVIEW for RLHF feedback (D-015).
    // SIMPLE_NOTE goes straight to COMPLETED (no human review needed) and is
    // therefore skipped here — service.py:379 sets status=COMPLETED directly.
    REVIEW: isSimpleNote
      ? "skipped"
      : status === "REVIEW"
        ? "active"
        : status === "COMPLETED"
          ? "completed"
          : "pending",

    // STORAGE: Completed when status is COMPLETED. Stays pending while in
    // REVIEW (user must act first). SIMPLE_NOTE: completes here directly
    // without traversing LLM/REVIEW — service.py:379 writes the Note row
    // with status=COMPLETED in one step.
    STORAGE: status === "COMPLETED" ? "completed" : "pending",

    // EMBEDDING: Generates 768-dim vector after storage. Dispatched by
    // _dispatch_enrichment_tasks() at service.py:705 ONLY on review accept/edit.
    // SIMPLE_NOTE never reviews → never embeds → never enters the RAG corpus.
    // DISCARDED notes also skip the dispatch (the action branch in review_note
    // is gated on accept/edit), so EMBEDDING and RECOMMEND must skip together —
    // showing EMBEDDING completed + RECOMMEND skipped at the same time would
    // contradict RECOMMEND's own rationale ("no embedding → no recs").
    // (Tradeoff: SIMPLE_NOTE saves Ollama RAM but cannot be retrieved by future
    // RAG queries. Revisit if SIMPLE_NOTE corpus gap becomes a search-quality
    // problem.)
    EMBEDDING:
      isSimpleNote || note.review_action === "DISCARDED"
        ? "skipped"
        : status === "COMPLETED"
          ? "completed"
          : "pending",

    // RECOMMEND: Post-pipeline enrichment. Skipped for SIMPLE_NOTE (no
    // embedding → no recs) and for DISCARDED notes (also no embedding).
    // Completed when note is COMPLETED and wasn't discarded.
    RECOMMEND: isSimpleNote
      ? "skipped"
      : status === "COMPLETED" && note.review_action !== "DISCARDED"
        ? "completed"
        : note.review_action === "DISCARDED"
          ? "skipped"
          : "pending",
  };
}

/**
 * Custom hook to derive PipelineState from a Note API response.
 *
 * @param note - Note API response (can be null/undefined while loading)
 * @returns PipelineState derived from note fields
 */
export function usePipelineState(
  note: NoteApiResponse | null | undefined,
): PipelineState | null {
  return useMemo(() => {
    if (!note) return null;

    return {
      noteId: note.id,
      stages: deriveStageStatus(note),
      classify: note.intent
        ? {
            intent: note.intent,
            confidence: note.intent_confidence ?? 0,
            routingDecision: getRoutingDecision(note.intent),
          }
        : undefined,
    };
  }, [note]);
}
